import {queryOptions, useQuery, useSuspenseQuery} from '@github-ui/react-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import type {SponsorDataResponse, SponsorableUser} from '../types'

function sponsorDataQueryOptions(ownerLogin: string, repoName: string, isOrgOwned: boolean) {
  return queryOptions({
    queryKey: [
      'repo-sponsor-data',
      {queryDeps: {pathname: `/${ownerLogin}/${repoName}/_sidebar/sponsors`}, isOrgOwned},
    ],
    queryFn: () =>
      queryFnFetch<SponsorDataResponse>({
        queryDeps: {pathname: `/${ownerLogin}/${repoName}/_sidebar/sponsors`},
      }),
  })
}

/**
 * Enriches sponsorables with `isOrg` derived from the repo owner context.
 * The repo owner's sponsorable entry gets `isOrg` from the caller-provided flag;
 * other sponsorables keep their server value or default to `false`.
 */
function enrichSponsorables(sponsorables: SponsorableUser[], ownerLogin: string, isOrgOwned: boolean) {
  return sponsorables.map(s => ({
    ...s,
    isOrg: s.isOrg ?? (s.login === ownerLogin ? isOrgOwned : false),
  }))
}

interface UseSponsorDataOptions {
  ownerLogin: string
  repoName: string
  /** Whether the repo owner is an org — used to derive `isOrg` on the owner's sponsorable. */
  isOrgOwned?: boolean
}

/**
 * Lazily fetches sponsor data (non-suspending).
 * Use in contexts where you don't want to block rendering (e.g. header buttons).
 */
export function useSponsorData({ownerLogin, repoName, isOrgOwned = false}: UseSponsorDataOptions) {
  const {data, isLoading, isError} = useQuery({
    ...sponsorDataQueryOptions(ownerLogin, repoName, isOrgOwned),
    select: sponsorData => ({
      ...sponsorData,
      sponsorables: enrichSponsorables(sponsorData.sponsorables, ownerLogin, isOrgOwned),
    }),
  })

  return {
    sponsorData: data ?? null,
    isLoading,
    isError,
  }
}

/**
 * Fetches sponsor data with Suspense support.
 * Use inside a <Suspense> boundary (e.g. sidebar sections).
 */
export function useSponsorDataSuspense({ownerLogin, repoName, isOrgOwned = false}: UseSponsorDataOptions) {
  const result = useSuspenseQuery({
    ...sponsorDataQueryOptions(ownerLogin, repoName, isOrgOwned),
    select: data => ({
      ...data,
      sponsorables: enrichSponsorables(data.sponsorables, ownerLogin, isOrgOwned),
    }),
  })

  return result
}
